import { Component, Input, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatSelect, MatSelectChange } from '@angular/material/select';
import { EditEmployerNotificationDialog, RecipientsFormGroup } from '@core/models/leave-admin/employers/employer-notification.model';
import { EmployerNotificationRecipient, EmployerNotificationRecipientType } from '@core/models/leave-admin/employers/employer-profile.model';

@Component({
    selector: 'app-add-notif-recipients',
    templateUrl: './add-notif-recipients.component.html',
    styleUrl: './add-notif-recipients.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddNotifRecipientsComponent {
  static readonly componentName = 'AddNotifRecipientsComponent';
  
  @Input() formGroup: FormGroup<RecipientsFormGroup>;
  @Input() dialogData: EditEmployerNotificationDialog;
  @Input() sysText: any;
  @Input() enabled: FormControl<boolean>;
  @Input() header: string;
  @Input() recipientBoxLabel: string;
  @Input() expanded: boolean;
  @Input() defaultRecipientTypeId: number = null; //nullable
  @Input() defaultAdditionalRecipientTypeId: number = null; //nullable
  @Input() usingDefaults: boolean; //nullable

  @ViewChild('select') private select: MatSelect;

  get recipients(): FormControl<EmployerNotificationRecipient[]> {
    return this.formGroup.controls.recipients;
  }

  get recipientTypeAdd(): FormControl<string[]> {
    return this.formGroup.controls.recipientTypeAdd;
  }

  get employeeAdd(): FormControl<string> {
    return this.formGroup.controls.employeeAdd;
  }

  get emailAddressAdd(): FormControl<string> {
    return this.formGroup.controls.emailAddressAdd;
  }

  public onRemoveRecipient(recipient: EmployerNotificationRecipient): void {
    // the pressed button goes with its chip
    this.select.focus();
    this.recipients.setValue(this.recipients.value
      .filter(r => { 
        if (r.type === EmployerNotificationRecipientType.specificEmailAddress) {
          return r.title !== recipient.title
        }
        else {
          return !(r.id == recipient.id && (r.emailAddressTypeId ?? 0) == (recipient.emailAddressTypeId ?? 0))
        }
      }));

    if (recipient.type === EmployerNotificationRecipientType.recipientType) {
      this.recipientTypeAdd.setValue(this.recipientTypeAdd.value
        .filter(key => key !== recipient.id + "." + recipient.emailAddressTypeId)
      );
    }
  }

  public onClickRecipientType(msc: MatSelectChange): void {
    // remove the previously selected items that are no longer selected

    this.recipients.value
    .filter(enr => enr.type == EmployerNotificationRecipientType.recipientType)
    .forEach(rec => {
      if (msc.value.find(key => key == rec.id + "." + rec.emailAddressTypeId) == null)
        this.removeContactChip(rec);
    });

    // add the selected items that were perviously not selected

    msc.value.forEach(key => {
      if (this.recipients.value.find(enr => key == enr.id + "." + enr.emailAddressTypeId) == null)
        this.addRecipientChip(
          EmployerNotificationRecipientType.recipientType,
          this.getRecipientTypeOptionId(key),
          this.getRecipientTypeOptionName(key),
          null,
          this.getRecipientTypeOptionDescription(key),
          this.getRecipientTypeOptionEmailAddressTypeId(key));
      });
  }

  public onAddEmployee(): void {
    const selectedOption = this.dialogData.employeeEmailOptions
      .find(eo => eo.id + "." + eo.emailAddressTypeId === this.employeeAdd.value);

    if (!this.recipients.value.find(r => r.id == selectedOption.id && (r.emailAddressTypeId ?? 0) == (selectedOption.emailAddressTypeId ?? 0))) {
      this.addRecipientChip(
        EmployerNotificationRecipientType.specificEmployee,
        selectedOption.id as number,
        selectedOption.description,
        selectedOption.companyName,
        "",
        selectedOption.emailAddressTypeId
      );
    }

    this.employeeAdd.setValue(null);
  }

  public onAddEmail(): void {
    const val = this.emailAddressAdd.value?.trim();

    if (val) {
      if (!this.recipients.value.find(v => v.title == val)) {
        this.addRecipientChip(
          EmployerNotificationRecipientType.specificEmailAddress,
          null,
          this.emailAddressAdd.value,
          null,
          null,
          null
        );

        this.emailAddressAdd.setValue(null);
      }
      else {
        this.emailAddressAdd.setErrors({msError: this.sysText.emailAddressMessage})
      }
    }
    else {
      this.emailAddressAdd.setValue(null);
    }
  }

  public getRecipientTypeOptionId(key: string): number {
    const recipientType = this.dialogData.recipientTypeOptions.find(o => o.id + "." + o.emailAddressTypeId == key);
    return recipientType?.id;
  }

  public getRecipientTypeOptionName(key: string): string {
    const recipientType = this.dialogData.recipientTypeOptions.find(o => o.id + "." + o.emailAddressTypeId == key);
    return recipientType?.name;
  }

   public getRecipientTypeOptionDescription(key: string): string {
    const recipientType = this.dialogData.recipientTypeOptions.find(o => o.id + "." + o.emailAddressTypeId == key);
    return recipientType?.description;
  }

  public getRecipientTypeOptionEmailAddressTypeId(key: string): number {
    const recipientType = this.dialogData.recipientTypeOptions.find(o => o.id + "." + o.emailAddressTypeId == key);
    return recipientType?.emailAddressTypeId;
  }

  private addRecipientChip(
    type: EmployerNotificationRecipientType,
    id: number, 
    title: string,
    companyName: string,
    description: string, 
    emailAddressTypeId: number): void {
    this.recipients.value.push({
      type: type,
      id: id,
      title: title,
      companyName: companyName,
      description: description,
      emailAddressTypeId: emailAddressTypeId
    });
  }

  private removeContactChip(employerNotificationRecipient: EmployerNotificationRecipient): void {
    this.recipients.setValue(
      this.recipients.value
        .filter(r => !(r.id == 
          employerNotificationRecipient.id 
          && (r.emailAddressTypeId ?? 0) == (employerNotificationRecipient.emailAddressTypeId ?? 0))));
  }
}